import { createApiAction, createAsyncActionRequestTypes } from "../../api";
import {
  TOGGLE_DEVCON,
  CHECK_EMERIT_DEVICES_STATUS,
  SET_EMERIT_V4_STATUSES,
  SET_DEVICE_AVAILABILITY,
  SET_DEVICE_ONLINE,
  RECONCILE_DEVICE_STATUS,
  RESYNC_V4_DEVICE_STATUSES,
  REFRESH_DEVICE_LOCATION,
  SET_DEVICE_LOCATION,
  SYNC_RUN_EVENT,
  FETCH_V4_ARC_DEVICE_LINKS,
  V4_ARC_DEVICE_LINKS_LOADED,
  CHECK_V4_ARC_DEVICE_LINK,
  V4_ARC_DEVICE_LINK_CHECKED,
} from "../constants/device.constants";

export const UPDATE_DEVICE = "UPDATE_DEVICE";
export const updateDevice = (device) => ({ type: UPDATE_DEVICE, device });
export const toggleDevCon = (isOpen) => ({ type: TOGGLE_DEVCON, isOpen });

export const MIGRATE_DEVICE_TO_V4 = "MIGRATE_DEVICE_TO_V4";
export const migrateDeviceToV4 = (deviceId) => ({
  deviceId,
  type: MIGRATE_DEVICE_TO_V4,
});

export const RUN_CONSOLIDATION = "RUN_CONSOLIDATION";
export const runConsolidation = (deviceId, strategy = "pull") => ({
  deviceId,
  strategy,
  type: RUN_CONSOLIDATION,
});

export const LOOKUP_DEVICE_ID =
  createAsyncActionRequestTypes("LOOKUP_DEVICE_ID");
export const lookupDeviceId = (deviceId) =>
  createApiAction(
    {
      url: `/migration/lookup/device_${deviceId}`,
    },
    LOOKUP_DEVICE_ID
  );

export const GET_DEVICE_V4 = createAsyncActionRequestTypes("GET_DEVICE_V4");
export const getDeviceV4 = (deviceId, headers) =>
  createApiAction(
    {
      url: `/devices/${deviceId}`,
      headers,
    },
    GET_DEVICE_V4
  );

export const GET_DEVICES_V4 = createAsyncActionRequestTypes("GET_DEVICES_V4");
export const getDevicesV4 = (headers) =>
  createApiAction(
    {
      url: `/devices`,
      headers,
    },
    GET_DEVICES_V4
  );

export const CREATE_DEVICE_V4 =
  createAsyncActionRequestTypes("CREATE_DEVICE_V4");
export const createDeviceV4 = (body, headers) =>
  createApiAction(
    {
      url: "/devices",
      method: "POST",
      body,
      headers,
    },
    CREATE_DEVICE_V4
  );

export const LINK_DEVICE_V4 = createAsyncActionRequestTypes("LINK_DEVICE_V4");
export const linkDeviceV4 = (deviceID, headers) =>
  createApiAction(
    {
      url: `/devices/${deviceID}/vendor-specific/emerit-link`,
      method: "POST",
      headers,
    },
    LINK_DEVICE_V4
  );

export const CONSOLIDATE_DEVICE_V4 = createAsyncActionRequestTypes(
  "CONSOLIDATE_DEVICE_V4"
);
export const consolidateDeviceV4 = (
  deviceID,
  strategy = "pull",
  rebootAfterCompletion = false,
  headers
) =>
  createApiAction(
    {
      url: `/device-config/${deviceID}/consolidate`,
      method: "POST",
      body: {
        strategy,
        reboot_after_completion: rebootAfterCompletion,
      },
      headers,
    },
    CONSOLIDATE_DEVICE_V4
  );

export const POST_DEFAULT_SETTINGS = createAsyncActionRequestTypes(
  "POST_DEFAULT_SETTINGS"
);
export const postDefaultSettings = (deviceID, headers) =>
  createApiAction(
    {
      url: `/device-config/${deviceID}/default-settings`,
      method: "POST",
      headers,
    },
    POST_DEFAULT_SETTINGS
  );

export const GET_CURRENT_CONFIG_SYNC_V4 = createAsyncActionRequestTypes(
  "GET_CURRENT_CONFIG_SYNC_V4"
);

export const getCurrentConfigSyncV4 = (deviceID) =>
  createApiAction(
    {
      url: `/device-config/${deviceID}/syncs/current-run`,
      method: "GET",
    },
    GET_CURRENT_CONFIG_SYNC_V4
  );

export const GET_DEVICE_SETTINGS_V4 = createAsyncActionRequestTypes(
  "GET_DEVICE_SETTINGS_V4"
);
export const getDeviceSettingsV4 = (deviceID, headers) =>
  createApiAction(
    {
      url: `/device-config/${deviceID}/settings`,
      method: "GET",
      headers,
    },
    GET_DEVICE_SETTINGS_V4
  );

export const START_UPDATE_SETTINGS = "START_UPDATE_SETTINGS";

export const startUpdateSettingsV4 = (settings) => ({
  type: START_UPDATE_SETTINGS,
  settings,
});

export const UPDATE_DEVICE_SETTINGS_V4 = createAsyncActionRequestTypes(
  "UPDATE_DEVICE_SETTINGS_V4"
);
export const updateDeviceSettingsV4 = (deviceID, setting, body) =>
  createApiAction(
    {
      url: `/device-config/${deviceID}/settings/${setting}`,
      method: "POST",
      body,
    },
    UPDATE_DEVICE_SETTINGS_V4
  );

// Re-read a device's V4 settings and clear DevCon's error flags if they come
// back — recovers a panel left in its error state by a run we stopped watching.
export const RELOAD_SETTINGS_V4 = "RELOAD_SETTINGS_V4";
export const reloadSettingsV4 = (deviceId) => ({
  deviceId,
  type: RELOAD_SETTINGS_V4,
});

export const RESTORE_DEFAULT_SETTINGS = "RESTORE_DEFAULT_SETTINGS";
export const restoreDefaultSettings = (deviceId) => ({
  deviceId,
  type: RESTORE_DEFAULT_SETTINGS,
});

// V4 reboot: POST /v1/devices/{uuid}/command/reboot. Optional ownerId/accountId
// let non-current-device callers (admin Manage overlay) scope explicitly.
export const REBOOT_DEVICE_V4 = "REBOOT_DEVICE_V4";
export const rebootDeviceV4 = (deviceId, ownerId, accountId) => ({
  deviceId,
  ownerId,
  accountId,
  type: REBOOT_DEVICE_V4,
});

// Generic V4 command via devicesCommandCreate: reboot | factory_restore |
// poweroff | request_firmware | request_location.
export const DEVICE_COMMAND_V4 = "DEVICE_COMMAND_V4";
export const deviceCommandV4 = (deviceId, command, ownerId, accountId) => ({
  deviceId,
  command,
  ownerId,
  accountId,
  type: DEVICE_COMMAND_V4,
});

export const INITIATE_PUSH_CONSOLIDATION = "INITIATE_PUSH_CONSOLIDATION";
export const initiatePushConsolidation = (
  deviceId,
  targetDeviceIds,
  onlyChanges,
  reboot = false
) => ({
  deviceId,
  targetDeviceIds,
  onlyChanges,
  reboot,
  type: INITIATE_PUSH_CONSOLIDATION,
});

export const PUSH_CONSOLIDATION =
  createAsyncActionRequestTypes("PUSH_CONSOLIDATION");
export const pushConsolidation = (deviceID, reboot = false) =>
  createApiAction(
    {
      url: `/device-config/${deviceID}/syncs`,
      method: "POST",
      body: {
        delay: 0,
        reboot_after_completion: reboot,
      },
    },
    PUSH_CONSOLIDATION
  );

export const GET_SYNC_STATUS = createAsyncActionRequestTypes("GET_SYNC_STATUS");
export const getSyncStatus = (deviceID, syncID, headers) =>
  createApiAction(
    {
      url: `/device-config/${deviceID}/syncs/${syncID}`,
      headers,
    },
    GET_SYNC_STATUS
  );

export const GET_ALL_SYNCS = createAsyncActionRequestTypes("GET_ALL_SYNCS");
export const getAllSyncs = (deviceID, headers) =>
  createApiAction(
    {
      url: `/device-config/${deviceID}/syncs`,
      headers,
    },
    GET_ALL_SYNCS
  );

export { CHECK_EMERIT_DEVICES_STATUS, SET_EMERIT_V4_STATUSES, SET_DEVICE_AVAILABILITY, SET_DEVICE_ONLINE, RECONCILE_DEVICE_STATUS, RESYNC_V4_DEVICE_STATUSES, REFRESH_DEVICE_LOCATION, SET_DEVICE_LOCATION, SYNC_RUN_EVENT };
export const checkEmeritDevicesStatus = (deviceIds) => ({
  type: CHECK_EMERIT_DEVICES_STATUS,
  deviceIds,
});
export const setEmeritV4Statuses = (statuses) => ({
  type: SET_EMERIT_V4_STATUSES,
  statuses,
});

export const fetchV4ArcDeviceLinks = () => ({ type: FETCH_V4_ARC_DEVICE_LINKS });
// `deviceIds`: V4 uuids of every device holding a link on some ARC.
export const v4ArcDeviceLinksLoaded = (deviceIds) => ({
  type: V4_ARC_DEVICE_LINKS_LOADED,
  deviceIds,
});
export const checkV4ArcDeviceLink = (uuid, ownerId, accountId) => ({
  type: CHECK_V4_ARC_DEVICE_LINK,
  uuid,
  ownerId,
  accountId,
});
// `linked`: null when the check failed.
export const v4ArcDeviceLinkChecked = (uuid, linked) => ({
  type: V4_ARC_DEVICE_LINK_CHECKED,
  uuid,
  linked,
});

export const setDeviceAvailability = (uuid, available) => ({
  type: SET_DEVICE_AVAILABILITY,
  uuid,
  available,
});

// `at`: epoch seconds of the connection change, shown as "connected/disconnected since".
// `reason`: V4 closed_reason, undefined when unknown.
export const setDeviceOnline = (uuid, online, at, reason) => ({
  type: SET_DEVICE_ONLINE,
  uuid,
  online,
  at,
  reason,
});

// `expected`: what the connection event claimed; undefined for a sign of life.
export const reconcileDeviceStatus = (uuid, expected, accountId) => ({
  type: RECONCILE_DEVICE_STATUS,
  uuid,
  expected,
  accountId,
});

export const resyncV4DeviceStatuses = (accountId) => ({
  type: RESYNC_V4_DEVICE_STATUSES,
  accountId,
});

// Asks the saga to fetch the device's latest fix (the websocket event carries
// only the uuid). accountId is a fallback scope when the device isn't in state.
export const refreshDeviceLocation = (uuid, accountId) => ({
  type: REFRESH_DEVICE_LOCATION,
  uuid,
  accountId,
});

export const setDeviceLocation = (uuid, position) => ({
  type: SET_DEVICE_LOCATION,
  uuid,
  position,
});

export const syncRunEvent = (deviceId, runId, status) => ({
  type: SYNC_RUN_EVENT,
  deviceId,
  runId,
  status,
});
